// @ts-nocheck
// SpacesScreen — Space management list (INTERNAL/PUBLIC partition).
// Lists every space in the org, links to membership management, and creates a
// new space (type chosen once and then locked). Mirrors the design-handoff
// prototype, built in the codebase's RN-primitive admin-screen idiom.
import React, { useState, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet, Platform, Pressable,
} from 'react-native';
import { Colors, FontWeight, Radii, Shadow } from '../../theme/tokens';
import { useApp, navigate } from '../../store/AppStore';
import { useResponsive } from '../../theme/responsive';
import { ScreenShell } from '../../components/WebShell';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { spacesService } from '../../services/spaces/spaces.service';
import { requesterSourceLabel } from '../../schema/spaces/space.schema';

// Web-only staggered fade-up entrance (react-native-web animationKeyframes).
function fadeUp(delayMs: number): any {
  if (Platform.OS !== 'web') return null;
  return {
    animationKeyframes: { '0%': { opacity: 0, transform: [{ translateY: 12 }] }, '100%': { opacity: 1, transform: [{ translateY: 0 }] } },
    animationDuration: '480ms',
    animationDelay: `${delayMs}ms`,
    animationFillMode: 'both',
    animationTimingFunction: 'cubic-bezier(.2,.7,.3,1)',
  };
}

// Square icon tile — globe (PUBLIC) on ink gradient, lock (INTERNAL) on teal gradient.
function SpaceTile({ internal, size = 34 }: { internal: boolean; size?: number }) {
  return (
    <View style={{
      width: size, height: size, borderRadius: Radii.r3, alignItems: 'center', justifyContent: 'center',
      backgroundColor: internal ? Colors.internal : Colors.ink,
      ...(Platform.OS === 'web' ? {
        backgroundImage: internal
          ? 'linear-gradient(135deg, #1F8A9E, #176475)'
          : 'linear-gradient(135deg, #3D3D55, #1C1C2E)',
        boxShadow: internal
          ? '0 6px 14px -6px rgba(31,138,158,0.55)'
          : '0 6px 14px -6px rgba(28,28,46,0.45)',
      } as any : {}),
    }}>
      <Icon name={internal ? 'Lock' : 'Globe'} size={size >= 34 ? 16 : 14} color="#fff" />
    </View>
  );
}

function StatusChip({ active }: { active: boolean }) {
  return (
    <View style={[S.chip, active ? { backgroundColor: Colors.successSoft, borderColor: Colors.successLine } : { backgroundColor: Colors.paper2, borderColor: Colors.line }]}>
      <View style={[S.chipDot, { backgroundColor: active ? Colors.success : Colors.ink4 }]} />
      <Text style={[S.chipText, { color: active ? Colors.success : Colors.ink3 }]}>{active ? 'Active' : 'Inactive'}</Text>
    </View>
  );
}

export function SpacesScreen() {
  const { spaces, allSpaces, createSpace } = useApp();
  const { pagePadX, isMobile } = useResponsive();
  const [showNew, setShowNew] = useState(false);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [hoverId, setHoverId] = useState<string | null>(null);

  // Admins get the full org list; everyone else sees the spaces they can enter.
  const list = (allSpaces && allSpaces.length ? allSpaces : spaces) || [];
  const internalCount = list.filter((s: any) => s.type === 'INTERNAL').length;

  // Member counts aren't on the space payload — fetch them in parallel (cheap,
  // a handful of spaces). Failures just leave the count blank.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(list.map(async (s: any) => {
        try { const res = await spacesService.listMembers(s.id); return [s.id, res.count ?? (res.data || []).length] as const; }
        catch { return [s.id, undefined] as const; }
      }));
      if (cancelled) return;
      const map: Record<string, number> = {};
      entries.forEach(([id, n]) => { if (n !== undefined) map[id] = n as number; });
      setMemberCounts(map);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  return (
    <ScreenShell crumbs={[{ label: 'Settings' }, { label: 'Account' }, { label: 'Spaces' }]}>
      <View style={S.root}>
        <View style={[S.pageHead, { paddingHorizontal: pagePadX }]}>
          <View style={{ flex: 1 }}>
            <Text style={S.pageTitle}>Spaces</Text>
            <Text style={S.pageSub}>A space is a hard PUBLIC / INTERNAL partition. Type is chosen once and locked. Access is governed by per-space membership — there is no org-chart inheritance.</Text>
          </View>
          <PrimaryButton title="New space" size="md" variant="primary"
            leftIcon={<Icon name="Plus" size={14} color="#fff" />} onPress={() => setShowNew(true)} />
        </View>

        <ScrollView style={S.body} contentContainerStyle={{ padding: isMobile ? 16 : 24, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
          {/* Trust banner */}
          <View style={[S.trustBanner, fadeUp(20)]}>
            <Icon name="Lock" size={16} color={Colors.internalInk} />
            <Text style={S.trustText}>
              <Text style={{ fontWeight: FontWeight.bold }}>INTERNAL</Text> spaces are a load-bearing trust boundary. Tickets, messages and requesters never cross the partition, and they're invisible to anyone without an explicit membership grant — including org-chart managers.
            </Text>
          </View>

          <View style={S.sectionH}>
            <Text style={S.sectionTitle}>All spaces</Text>
            <Text style={S.sectionSub}>{list.length} space{list.length === 1 ? '' : 's'} · {internalCount} internal · spaces are deactivated, never deleted</Text>
          </View>

          <View style={[S.card, fadeUp(70)]}>
            {/* Header row */}
            <View style={[S.row, S.rowHead]}>
              <Text style={[S.th, { flex: 1 }]}>Space</Text>
              <Text style={[S.th, S.colType]}>Type</Text>
              {!isMobile && <Text style={[S.th, S.colSource]}>Requester source</Text>}
              {!isMobile && <Text style={[S.th, S.colMembers, { textAlign: 'center' }]}>Members</Text>}
              <Text style={[S.th, S.colStatus]}>Status</Text>
              <View style={S.colChevron} />
            </View>
            {list.map((s: any, i: number) => {
              const internal = s.type === 'INTERNAL';
              return (
                <TouchableOpacity key={s.id} activeOpacity={0.7}
                  onPress={() => navigate('/admin/settings/spaces/' + s.id)}
                  {...(Platform.OS === 'web' ? { onMouseEnter: () => setHoverId(s.id), onMouseLeave: () => setHoverId(null) } as any : {})}
                  style={[
                    S.row,
                    i < list.length - 1 && S.rowBorder,
                    { opacity: s.isActive ? 1 : 0.6 },
                    hoverId === s.id && {
                      backgroundColor: Colors.paper2,
                      ...(Platform.OS === 'web' ? { boxShadow: `inset 3px 0 0 ${internal ? Colors.internal : Colors.accent}` } as any : {}),
                    },
                    fadeUp(120 + i * 70),
                  ]}>
                  <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <SpaceTile internal={internal} />
                    <View style={{ flex: 1 }}>
                      <Text style={S.spaceName} numberOfLines={1}>{s.name}</Text>
                      <Text style={S.spaceMeta}>{(s.key || '').toUpperCase()} · created {s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</Text>
                    </View>
                  </View>
                  <View style={S.colType}>
                    {internal ? (
                      <View style={S.lockup}>
                        <Icon name="Lock" size={11} color="#fff" />
                        <Text style={S.lockupText}>INTERNAL</Text>
                        <Text style={S.lockupKey}>{(s.key || '').toUpperCase()}</Text>
                      </View>
                    ) : (
                      <View style={[S.chip, { backgroundColor: Colors.card, borderColor: Colors.line }]}>
                        <Icon name="Globe" size={11} color={Colors.ink2} />
                        <Text style={[S.chipText, { color: Colors.ink2 }]}>Public</Text>
                      </View>
                    )}
                  </View>
                  {!isMobile && <Text style={[S.mono, S.colSource]}>{requesterSourceLabel(s.requesterSource)}</Text>}
                  {!isMobile && <Text style={[S.mono, S.colMembers, { textAlign: 'center', color: Colors.ink2 }]}>{memberCounts[s.id] ?? '·'}</Text>}
                  <View style={S.colStatus}><StatusChip active={!!s.isActive} /></View>
                  <View style={S.colChevron}><Icon name="ChevronR" size={14} color={Colors.ink4} /></View>
                </TouchableOpacity>
              );
            })}
            {list.length === 0 && (
              <View style={[S.row, { justifyContent: 'center' }]}><Text style={{ color: Colors.ink3, padding: 20 }}>No spaces yet.</Text></View>
            )}
          </View>
        </ScrollView>
      </View>

      {showNew && (
        <NewSpaceModal
          existingKeys={list.map((s: any) => (s.key || '').toUpperCase())}
          onCancel={() => setShowNew(false)}
          onCreate={async (payload: any) => { const s = await createSpace(payload); if (s) setShowNew(false); }}
        />
      )}
    </ScreenShell>
  );
}

// ─── New space modal — type chosen once, then locked ───
function NewSpaceModal({ existingKeys, onCancel, onCreate }) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [type, setType] = useState<'PUBLIC' | 'INTERNAL'>('INTERNAL');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!keyTouched) setKey(name.toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 6));
  }, [name, keyTouched]);

  const keyError = !key ? null
    : !/^[A-Z][A-Z0-9]*$/.test(key) ? 'Uppercase letters and digits, must start with a letter.'
    : existingKeys.includes(key) ? 'A space with this key already exists.'
    : null;
  const canSubmit = name.trim() && key && !keyError && !submitting;
  const requesterSource = type === 'INTERNAL' ? 'SK People' : 'SK CRM';

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    await onCreate({ name: name.trim(), key, type });
    setSubmitting(false);
  };

  const TYPE_OPTS = [
    { v: 'PUBLIC', icon: 'Globe', title: 'Public', sub: 'Customer-facing. Requesters resolve from SK CRM.' },
    { v: 'INTERNAL', icon: 'Lock', title: 'Internal', sub: 'Staff-only partition. Requesters resolve from SK People.' },
  ] as const;

  return (
    <View style={S.backdrop}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onCancel} />
      <View style={[S.modal, Platform.OS === 'web' ? { width: 600, maxHeight: '92vh' } as any : { width: '96%' }]}>
        <View style={S.modalHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={S.modalHeadIcon}><Icon name="Layers" size={16} color={Colors.internal} /></View>
            <View>
              <Text style={S.modalTitle}>New space</Text>
              <Text style={S.modalSub}>You'll be taken to membership management after creation.</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onCancel} style={S.iconBtn}><Icon name="X" size={15} color={Colors.ink2} /></TouchableOpacity>
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 22, gap: 18 }}>
          <View style={{ flexDirection: 'row', gap: 14 }}>
            <View style={{ flex: 2 }}>
              <Text style={S.label}>Space name *</Text>
              <TextInput value={name} onChangeText={setName} autoFocus
                placeholder="e.g. People — HR Helpdesk" placeholderTextColor={Colors.ink4} style={S.input} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={S.label}>Key *</Text>
              <TextInput value={key} onChangeText={(v) => { setKeyTouched(true); setKey(v.toUpperCase()); }}
                placeholder="HR" placeholderTextColor={Colors.ink4} style={[S.input, S.mono]} />
              {keyError ? <Text style={S.errText}>{keyError}</Text> : <Text style={S.hintText}>Short prefix.</Text>}
            </View>
          </View>

          {/* Type — chosen ONCE, then immutable */}
          <View>
            <Text style={[S.label, { marginBottom: 2 }]}>Partition type *</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 8 }}>
              <Icon name="Lock" size={11} color={Colors.ink3} />
              <Text style={S.hintText}>Locked after creation — it defines the trust boundary and the requester source.</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 10 }}>
              {TYPE_OPTS.map(o => {
                const on = type === o.v;
                const isI = o.v === 'INTERNAL';
                const onColor = isI ? Colors.internal : Colors.accent;
                const onBg = isI ? Colors.internalSoft : Colors.accentSoft;
                const onInk = isI ? Colors.internalInk : Colors.accentInk;
                return (
                  <TouchableOpacity key={o.v} activeOpacity={0.8} onPress={() => setType(o.v)}
                    style={{
                      flex: 1, padding: 14, borderRadius: Radii.r3, borderWidth: 1.5,
                      borderColor: on ? onColor : Colors.line, backgroundColor: on ? onBg : Colors.card,
                    }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Icon name={o.icon} size={15} color={on ? onInk : Colors.ink2} />
                      <Text style={{ fontWeight: FontWeight.semibold, fontSize: 13.5, color: on ? onInk : Colors.ink2 }}>{o.title}</Text>
                      {on && <View style={{ marginLeft: 'auto' }}><Icon name="Check" size={14} color={onInk} /></View>}
                    </View>
                    <Text style={{ fontSize: 11.5, color: Colors.ink3, marginTop: 5, lineHeight: 16 }}>{o.sub}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Derived requester source (read-only) */}
          <View style={S.derivedRow}>
            <Icon name="Users" size={15} color={Colors.ink3} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12.5, fontWeight: FontWeight.medium, color: Colors.ink }}>Requester source</Text>
              <Text style={{ fontSize: 11.5, color: Colors.ink3 }}>Derived from type · not editable</Text>
            </View>
            <Text style={[S.mono, S.derivedCode]}>{requesterSource}</Text>
          </View>
        </ScrollView>

        <View style={S.modalFoot}>
          <PrimaryButton title="Cancel" size="md" variant="default" onPress={onCancel} />
          <PrimaryButton title="Create space" size="md" variant="primary"
            leftIcon={<Icon name="ArrowRight" size={13} color="#fff" />} onPress={submit} disabled={!canSubmit} />
        </View>
      </View>
    </View>
  );
}

const S = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, paddingVertical: 20, backgroundColor: Colors.card, borderBottomWidth: 1, borderBottomColor: Colors.line, flexWrap: 'wrap' },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 12.5, color: Colors.ink3, marginTop: 4, maxWidth: 720, lineHeight: 18 },
  body: { flex: 1 },

  trustBanner: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, padding: 16, marginBottom: 16, backgroundColor: Colors.internalSoft, borderWidth: 1, borderColor: Colors.internalLine, borderLeftWidth: 3, borderLeftColor: Colors.internal, borderRadius: Radii.r4, ...(Shadow.sm as object) },
  trustText: { flex: 1, fontSize: 12.5, color: Colors.internalInk, lineHeight: 18 },

  sectionH: { marginBottom: 10 },
  sectionTitle: { fontSize: 14, fontWeight: FontWeight.semibold, color: Colors.ink },
  sectionSub: { fontSize: 12, color: Colors.ink3, marginTop: 2 },

  card: { backgroundColor: Colors.card, borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r4, overflow: 'hidden', ...(Shadow.base as object) },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 15, paddingHorizontal: 18, gap: 16, ...(Platform.OS === 'web' ? { transitionProperty: 'background-color, box-shadow', transitionDuration: '140ms' } as any : {}) },
  rowHead: { backgroundColor: Colors.paper2, paddingVertical: 9 },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  th: { fontSize: 11, fontWeight: FontWeight.medium, color: Colors.ink3, letterSpacing: 0.4, textTransform: 'uppercase' },
  colType: { width: 172, paddingRight: 12 },
  colSource: { width: 150 },
  colMembers: { width: 80 },
  colStatus: { width: 104 },
  colChevron: { width: 24, alignItems: 'flex-end' },

  spaceName: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  spaceMeta: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 11, color: Colors.ink3, marginTop: 1 },
  mono: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 11.5, color: Colors.ink2 },

  chip: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 22, paddingHorizontal: 9, borderRadius: Radii.full, borderWidth: 1, alignSelf: 'flex-start' },
  chipDot: { width: 6, height: 6, borderRadius: 3 },
  chipText: { fontSize: 11, fontWeight: FontWeight.medium },

  lockup: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 22, paddingHorizontal: 9, borderRadius: Radii.full, backgroundColor: Colors.internal, alignSelf: 'flex-start' },
  lockupText: { fontSize: 11, fontWeight: FontWeight.semibold, color: '#fff', letterSpacing: 0.3 },
  lockupKey: { fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, fontSize: 11, fontWeight: FontWeight.bold, color: '#fff', opacity: 0.85 },

  // Modal
  backdrop: { ...StyleSheet.absoluteFillObject, position: 'fixed' as any, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 100 },
  modal: { backgroundColor: Colors.card, borderRadius: Radii.r4, overflow: 'hidden', maxWidth: '100%', ...(Platform.OS === 'web' ? { boxShadow: '0 16px 48px rgba(16,24,40,0.24)' } as any : {}) },
  modalHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 22, borderBottomWidth: 1, borderBottomColor: Colors.line },
  modalHeadIcon: { width: 32, height: 32, borderRadius: Radii.r2, backgroundColor: Colors.internalSoft, alignItems: 'center', justifyContent: 'center' },
  modalTitle: { fontSize: 18, fontWeight: FontWeight.semibold, color: Colors.ink },
  modalSub: { fontSize: 11.5, color: Colors.ink3, marginTop: 2 },
  iconBtn: { width: 26, height: 26, borderRadius: Radii.r2, alignItems: 'center', justifyContent: 'center' },
  modalFoot: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, padding: 22, paddingVertical: 14, borderTopWidth: 1, borderTopColor: Colors.line, backgroundColor: Colors.paper2 },

  label: { fontSize: 12, fontWeight: FontWeight.medium, color: Colors.ink2, marginBottom: 4 },
  input: { height: 34, paddingHorizontal: 12, borderWidth: 1, borderColor: Colors.line, backgroundColor: Colors.card, borderRadius: Radii.r2, fontSize: 13, color: Colors.ink },
  hintText: { fontSize: 11, color: Colors.ink3 },
  errText: { fontSize: 11, color: Colors.danger },
  derivedRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: Colors.paper2, borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r2 },
  derivedCode: { fontSize: 12, color: Colors.ink2, backgroundColor: Colors.card, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: Colors.line, borderRadius: Radii.r1 },
});
